/**
 * <ry-toast>
 *
 * Toast notification component with static API.
 *
 * Usage (declarative):
 * <ry-toast variant="success">Saved successfully!</ry-toast>
 *
 * Usage (programmatic):
 * RyToast.success('Saved!');
 * RyToast.error('Failed to save');
 * RyToast.info('Processing...');
 * RyToast.warning('Are you sure?');
 *
 * JS and structure CSS use data-ry-target, theme CSS uses classes.
 */

import { RyElement } from '../core/ry-element.js';
import type { ToastVariant, ToastOptions } from '../types.js';

const DEFAULT_DURATION = 4000;

export class RyToast extends RyElement {
  #timeout: ReturnType<typeof setTimeout> | null = null;

  static observedAttributes = ['variant', 'duration'] as const;

  // Static container for programmatic toasts
  static #container: HTMLElement | null = null;

  static #getContainer(): HTMLElement {
    if (!RyToast.#container) {
      RyToast.#container = document.createElement('div');
      RyToast.#container.setAttribute('data-ry-target', 'toast-container');
      RyToast.#container.className = 'ry-toast-container'; // Keep for theme CSS
      document.body.appendChild(RyToast.#container);
    }
    return RyToast.#container;
  }

  // Static API
  static show(options: ToastOptions): RyToast {
    const toast = document.createElement('ry-toast') as RyToast;
    toast.textContent = options.message;

    if (options.variant) {
      toast.setAttribute('variant', options.variant);
    }
    if (options.duration !== undefined) {
      toast.setAttribute('duration', String(options.duration));
    }

    RyToast.#getContainer().appendChild(toast);
    return toast;
  }

  static success(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'success', duration });
  }

  static error(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'error', duration });
  }

  static info(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'info', duration });
  }

  static warning(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'warning', duration });
  }

  setup(): void {
    this.#transformMarkup();

    // Close button (use [close] attribute)
    const closeBtn = this.$('[close]');
    if (closeBtn) {
      this.on(closeBtn, 'click', () => this.dismiss());
    }

    // Auto-dismiss
    const duration = parseInt(this.getAttribute('duration') ?? String(DEFAULT_DURATION), 10);
    if (duration > 0) {
      this.#timeout = setTimeout(() => this.dismiss(), duration);
    }

    // Animate in (CSS expects 'visible' state)
    requestAnimationFrame(() => {
      this.state = 'visible';
    });
  }

  #transformMarkup(): void {
    // Check if already transformed
    if (this.$('[data-ry-target="content"]')) return;

    const variant = this.getAttribute('variant') ?? 'info';
    const message = this.textContent?.trim() ?? '';

    const icon = this.#getIcon(variant as ToastVariant);

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="icon" class="ry-toast__icon">${icon}</div>
      <div data-ry-target="content" class="ry-toast__content">${message}</div>
      <button data-ry-target="close" class="ry-toast__close" close aria-label="Dismiss">&times;</button>
    `;
  }

  #getIcon(variant: ToastVariant): string {
    const icons: Record<ToastVariant, string> = {
      info: 'ℹ',
      success: '✓',
      warning: '⚠',
      error: '✕'
    };
    return icons[variant] ?? icons.info;
  }

  dismiss(): void {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.state = 'hiding';

    // Remove after animation
    setTimeout(() => {
      this.remove();
    }, 300);

    this.emit('close');
  }

  teardown(): void {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }
  }
}

customElements.define('ry-toast', RyToast);
